import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { CmsService } from '../../core/services/cms.service';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { Layout } from '../../shared/model/layout.model';
import { Homepage } from '../../shared/model/homepage.model';
import { CatalogService } from '../../core/services/catalog.service';
import { ProductCategory } from '../../shared/model/product-category.model';
import { BreadcrumbsService } from '../../core/services/breadcrumbs.service';
import { Product } from '../../shared/model/product.model';
import { ProductService } from '../../core/services/product.service';
import { SeoService } from '../../core/services/seo.service';
import { GtmService } from '../../core/services/gtm.service';
import { CartEntry } from '../../shared/model/cart.model';
import { LoadingService } from '../../core/services/loading.service';

@Component({
  selector: 'raf-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomepageComponent implements OnInit, OnDestroy {
  layoutLoading$: Observable<boolean>;
  layout$: Observable<Layout | null>;
  homepageData$: BehaviorSubject<Homepage | null> = new BehaviorSubject<Homepage | null>(null);
  homepageDataLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  productCategoriesLoading$: Observable<boolean>;
  productCategories$: Observable<ProductCategory[] | null>;
  promotedProducts$: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);
  promotedCategories$: BehaviorSubject<ProductCategory[]> = new BehaviorSubject<ProductCategory[]>([]);
  promotedProductsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  promotedCategoriesLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private cmsService: CmsService,
    private catalogService: CatalogService,
    private breadcrumbService: BreadcrumbsService,
    private productService: ProductService,
    private seoService: SeoService,
    private gtm: GtmService,
    private loadingService: LoadingService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.layoutLoading$ = this.cmsService.layout$.pipe(map((resource) => resource.loading));
    this.layout$ = this.cmsService.layout$.pipe(map((resource) => resource.data));

    this.productCategoriesLoading$ = this.catalogService.categories$.pipe(map((resource) => resource.loading));
    this.productCategories$ = this.catalogService.categories$.pipe(map((resource) => resource.data));
  }

  ngOnInit(): void {
    this.getHomepage();
    this.breadcrumbService.setBreadcrumbs([]);
    this.seoService.setGeneralMetaTags();
    this.seoService.setTitle();
  }

  getHomepage(): void {
    this.cmsService
      .getHomepage()
      .subscribe((val) => {
        this.homepageData$.next(val);
        this.getPromotedProducts(val?.promotedProducts?.map((val) => val.productCode));
        this.getPromotedCategories(val?.promotedCategories?.map((val) => val.categoryCode));
      })
      .add(() => this.homepageDataLoading$.next(false));
  }

  getPromotedProducts(productIds?: string[]): void {
    if (productIds?.length) {
      this.promotedProductsLoading$.next(true);

      this.productService
        .getProducts({ product: productIds, pageSize: 8 })
        .subscribe((result) => {
          // sort results by original array
          result.items.sort((a, b) => {
            return productIds.indexOf(a.code) - productIds.indexOf(b.code);
          });
          this.promotedProducts$.next(result.items);
          this.logGtm(result.items);
        })
        .add(() => {
          this.promotedProductsLoading$.next(false);
          this.loadingService.loadingFinished();
        });
    }
  }

  getPromotedCategories(categoryIds?: string[]): void {
    if (categoryIds?.length) {
      this.promotedCategoriesLoading$.next(true);

      this.catalogService
        .getCategoriesById(categoryIds)
        .subscribe((result) => {
          this.promotedCategories$.next(result);
        })
        .add(() => this.promotedCategoriesLoading$.next(false));
    }
  }

  onProductSelected($event: { product: Product | CartEntry; index: number }) {
    this.logGtmProductSelected($event.product as Product, $event.index);
  }

  private logGtm(products: Product[]): void {
    this.gtm.viewItemList('homepage', 'Homepage', products);
  }

  private logGtmProductSelected(product: Product, index: number): void {
    this.gtm.selectItem('homepage', 'Homepage', product, index);
  }

  ngOnDestroy() {
    this.seoService.clearGeneralMetaTags();
  }
}
